import { Component, EventEmitter, Injector, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../core/components/base/base.component';
import { NETWORK_SPECIALITIES, TITLES } from './../../../core/data/app.data';
import { ColleagueService } from './../../../core/services/colleague.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { colleagueToTeamMember, searchColleagues } from './../../../core/utils/colleague-search';
import { patientReportedLabel, withPatientReported } from '../../../core/utils/patient-reported';

@Component({
  selector: 'app-medical-team-list-creator',
  templateUrl: './medical-team-list-creator.component.html',
  styleUrls: ['./medical-team-list-creator.component.scss']
})
export class MedicalTeamListCreatorComponent extends BaseComponent implements OnInit {
  /** The "Patient reported" mark of a row the patient gave (their invite, or the patient app). */
  readonly reported = patientReportedLabel;

  @Input() medicalTeam: any[] = [];
  /**
   * Offer the practice's colleagues when adding a doctor. Off by default: the invite wizard is
   * patient facing and must not see the practice's contacts.
   */
  @Input() colleagueSearch = false;
  @Output() teamChanged = new EventEmitter<any[]>();

  editingTeamMember: any = {};
  titles = TITLES;
  specialities = NETWORK_SPECIALITIES;

  /** The practice's colleagues (Settings > MedicAI Network), offered when adding a doctor. */
  colleagues: any[] = [];
  colleagueQuery = '';
  colleagueMatches: any[] = [];

  @ViewChild('addTeamMemberDialog') addTeamMemberDialog?: TemplateRef<any>;

  constructor(fs: FeedbackService, md: MatDialog, private injector: Injector) {
    super(fs, md);
  }

  ngOnInit(): void {
    // Looked up only when wanted: creating ColleagueService fetches the practice's colleagues.
    if (!this.colleagueSearch) { return; }
    this.injector.get(ColleagueService).colleagues$.pipe(takeUntil(this.destroy$)).subscribe(colleagues => {
      this.colleagues = Array.isArray(colleagues) ? colleagues : [];
      this.onColleagueSearch(this.colleagueQuery);
    });
  }

  openAddTeamMemberDialog(isGP?: boolean): void {
    this.editingTeamMember = isGP ? { id: this.medicalTeam.length + 1, speciality: 'General Practice', title: 'DR' } : { id: this.medicalTeam.length + 1 };
    this.onColleagueSearch('');
    this.dialog.open(this.addTeamMemberDialog!);
  }

  onColleagueSearch(query: string): void {
    this.colleagueQuery = typeof query === 'string' ? query : '';
    this.colleagueMatches = searchColleagues(this.colleagues, this.colleagueQuery, this.medicalTeam);
  }

  /** Fills the form from a colleague; a GP being added stays General Practice if the colleague has no speciality. */
  pickColleague(colleague: any): void {
    const member = colleagueToTeamMember(colleague);
    this.editingTeamMember = {
      ...this.editingTeamMember,
      ...member,
      title: member.title || this.editingTeamMember.title,
      speciality: member.speciality || this.editingTeamMember.speciality,
    };
    this.colleagueQuery = '';
  }

  /** A doctor being added rather than edited (new entries carry a numeric placeholder id). */
  get isNewMember(): boolean {
    return typeof this.editingTeamMember?.id === 'number';
  }

  colleagueLabel(colleague: any): string {
    return [colleague.title, colleague.name, colleague.surname].filter(Boolean).join(' ');
  }

  getTooltipText(member: any): string {
    return withPatientReported(`Speciality: ${member.speciality || 'Not Provided'}, Practice Number: ${member.practiceNumber || 'Not Provided'}, Email: ${member.email || 'Not Provided'}, Phone: ${member.phone || 'Not Provided'}`, member);
  }

  editTeamMember(member: any): void {
    this.editingTeamMember = { ...member };
    this.dialog.open(this.addTeamMemberDialog!);
  }

  saveTeamMember(): void {
    if (this.editingTeamMember.name && this.editingTeamMember.surname) {
      const index = this.medicalTeam.findIndex(c => c.id === this.editingTeamMember.id);
      if (index !== -1) {
        this.medicalTeam[index] = this.editingTeamMember;
      } else {
        this.medicalTeam.push(this.editingTeamMember);
      }
      if (this.editingTeamMember.isReferring) {
        this.medicalTeam.forEach(member => {
          if (member !== this.editingTeamMember) {
            member.isReferring = false;
          }
        });
      }
      this.emitMedicalTeam();
      this.dialog.closeAll();
    }
  }


  cancelEdit(): void {
    this.dialog.closeAll();
  }

  removeTeamMember(member: any): void {
    const index = this.medicalTeam.indexOf(member);
    if (index !== -1) {
      this.medicalTeam.splice(index, 1);
      this.emitMedicalTeam();
    }
  }

  private emitMedicalTeam(): void {
    const emittedTeam = this.medicalTeam.map(member => {
      if (typeof member.id === 'number') {
        const { id, ...rest } = member;
        return rest;
      }
      return member;
    });
    this.teamChanged.emit(emittedTeam);
  }
}
